import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "@/agents-v2/utils/cn";
import {
  usePromptRecipes,
  useRecipeCategories,
  type PromptRecipe,
  type RecipeCategory
} from "./prompt-recipes";

/**
 * The "/" slash glyph used on every recipe row and on the selected-skill chip
 * (matches the design mockup).
 * @returns The slash SVG icon.
 */
export const SlashGlyph: React.FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="16" y1="5" x2="8" y2="19" />
  </svg>
);

type TabKey = "all" | RecipeCategory;

interface PromptRecipesPopupProps {
  /** Whether the popup is open. */
  open: boolean;
  /** Close the popup (Esc, outside-click, or the X button). */
  onClose: () => void;
  /** Fired with the chosen recipe. */
  onSelect: (recipe: PromptRecipe) => void;
  /**
   * The chat textarea. Keyboard navigation (↑/↓/Enter/Esc) is bound to it in
   * the capture phase so it runs before the chat's own key handling — focus
   * stays in the textarea while the user steers the list.
   */
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  /**
   * The category most relevant to the page this chat is embedded in. In the
   * "All" view its recipes are listed first (the rest follow in canonical
   * order), so the page-relevant skills surface at the top. E.g. the answers
   * page passes "review"; the writing-plan page would pass "plan". Defaults to
   * the canonical category order when omitted.
   */
  primaryCategory?: RecipeCategory;
  /** A curated list instead of the full catalogue (the Word pane passes its own). */
  recipes?: PromptRecipe[];
  /**
   * When given, the popup renders in a portal, fixed to the viewport just
   * above this element instead of absolutely inside the composer. The Word
   * pane's composer sits inside the thread's scroll area, which clipped the
   * popup at the top and cut its shadow at the sides; a portal escapes that
   * and the list height adapts to the room above the anchor.
   */
  anchorRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Prompt recipes popup — a "/"-triggered command palette for the writing-plan
 * chat. Lists curated prompt presets grouped by category, filterable by tab.
 * Selecting one fires `onSelect` with the chosen recipe (does not auto-send).
 *
 * Visual design mirrors "Chat Prompt Picker v3" (Version A), rebuilt with the
 * app's Tailwind palette so it sits natively in the existing chat UI.
 * @param root0 - The props object
 * @param root0.open - Whether the popup is open
 * @param root0.onClose - Close the popup (Esc, outside-click, or the X button)
 * @param root0.onSelect - Fired with the chosen recipe
 * @param root0.textareaRef - The chat textarea, used for capture-phase keyboard navigation
 * @param root0.primaryCategory - Page-relevant category listed first in the "All" view
 * @returns The rendered popup, or null when closed
 */
const PromptRecipesPopup: React.FC<PromptRecipesPopupProps> = ({
  open,
  onClose,
  onSelect,
  textareaRef,
  primaryCategory,
  recipes: recipesProp,
  anchorRef
}) => {
  // The pane has no i18n layer; the web app's default strings are the strings.
  const t = (_key: string, options: { defaultValue: string }) => options.defaultValue;
  const allRecipes = usePromptRecipes();
  const recipes = recipesProp ?? allRecipes;
  const allCategories = useRecipeCategories();
  const categories = allCategories.filter((c) => recipes.some((r) => r.category === c.key));
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [activeIndex, setActiveIndex] = useState(0);
  const [entered, setEntered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Portal mode: where the anchor sits, re-measured while open.
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!open || !anchorRef) return;
    const measure = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (rect) setAnchorRect(rect);
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    const observer = typeof ResizeObserver !== "undefined" && anchorRef.current ? new ResizeObserver(measure) : null;
    if (observer && anchorRef.current) observer.observe(anchorRef.current);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      observer?.disconnect();
    };
  }, [open, anchorRef]);

  // Ordered tab keys for left/right keyboard navigation.
  const tabKeys = useMemo<TabKey[]>(
    () => ["all", ...categories.map((c) => c.key)],
    [categories]
  );

  // Category render order for the "All" view: the page-relevant category first,
  // then the rest in canonical order. The list and keyboard nav both follow
  // this, so the highlighted row always matches what's on screen.
  const orderedCategories = useMemo(() => {
    if (!primaryCategory) return categories;
    const primary = categories.find((c) => c.key === primaryCategory);
    if (!primary) return categories;
    return [primary, ...categories.filter((c) => c.key !== primaryCategory)];
  }, [primaryCategory, categories]);

  /** Recipes visible under the current tab, in display order. */
  const visibleRecipes = useMemo<PromptRecipe[]>(
    () =>
      activeTab === "all"
        ? orderedCategories.flatMap((c) =>
            recipes.filter((r) => r.category === c.key)
          )
        : recipes.filter((r) => r.category === activeTab),
    [activeTab, orderedCategories, recipes]
  );

  // Reset transient state each time the popup opens; kick off the enter anim.
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    setActiveTab("all");
    setActiveIndex(0);
    // Scroll to top so the first item is always visible at open, not the bottom.
    if (listRef.current) listRef.current.scrollTop = 0;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Keep the active index in range when the visible list changes (tab switch).
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(visibleRecipes.length - 1, 0)));
  }, [visibleRecipes.length]);

  const selectRecipe = useCallback(
    (recipe: PromptRecipe | undefined) => {
      if (recipe) onSelect(recipe);
    },
    [onSelect]
  );

  // Keyboard navigation — bound to the textarea in the capture phase so it
  // intercepts before the chat's bubble-phase onKeyDown (which sends on Enter).
  useEffect(() => {
    if (!open) return;
    const ta = textareaRef.current;
    if (!ta) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, visibleRecipes.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        setActiveTab((t) => tabKeys[(tabKeys.indexOf(t) + 1) % tabKeys.length]);
        setActiveIndex(0);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        setActiveTab((t) => tabKeys[(tabKeys.indexOf(t) - 1 + tabKeys.length) % tabKeys.length]);
        setActiveIndex(0);
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        selectRecipe(visibleRecipes[activeIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };

    ta.addEventListener("keydown", handler, true);
    return () => ta.removeEventListener("keydown", handler, true);
  }, [open, textareaRef, visibleRecipes, activeIndex, selectRecipe, onClose, tabKeys]);

  // Scroll the active row into view as it changes.
  useEffect(() => {
    if (!open) return;
    const el = itemRefs.current[activeIndex];
    el?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, open]);

  // Close on outside click (ignore the textarea so "/" toggling stays smooth).
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (textareaRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open, onClose, textareaRef]);

  if (!open) return null;
  if (anchorRef && !anchorRect) return null; // first measurement pending

  // Render rows with category headings only in "All" view.
  let renderIndex = -1;

  // Portal mode: pinned above the anchor, as wide as it, no taller than the
  // room above (header + tabs take ~88px; the list gets the rest, never
  // below 140px so at least two rows show on a very short pane).
  const GAP = 8;
  const CHROME = 88;
  const portalStyle: React.CSSProperties | undefined = anchorRect
    ? {
        position: "fixed",
        left: anchorRect.left,
        width: anchorRect.width,
        bottom: Math.max(GAP, window.innerHeight - anchorRect.top + GAP),
        maxHeight: Math.max(CHROME + 140, anchorRect.top - GAP * 2)
      }
    : undefined;
  const listMaxHeight = portalStyle ? Math.max(140, (portalStyle.maxHeight as number) - CHROME) : 330;

  const popup = (
    <div
      ref={containerRef}
      role="listbox"
      aria-label={t("promptRecipes.chrome.header", { defaultValue: "Prompt recipes" })}
      style={portalStyle}
      className={cn(
        portalStyle ? "z-[60] flex flex-col overflow-hidden" : "absolute bottom-[calc(100%+0.75rem)] left-0 right-0 z-20 overflow-hidden",
        "rounded-2xl border border-border bg-popover text-popover-foreground shadow-[0_8px_32px_rgba(0,0,0,0.10),0_2px_8px_rgba(0,0,0,0.06)]",
        "origin-bottom transition-all duration-200 ease-out",
        entered ? "translate-y-0 opacity-100" : "translate-y-2 opacity-0"
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-3.5 py-2.5">
        <span className="text-[10.5px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
          {t("promptRecipes.chrome.header", { defaultValue: "Prompt recipes" })}
        </span>
        <button
          type="button"
          onClick={onClose}
          title={t("promptRecipes.chrome.close", { defaultValue: "Close prompt recipes" })}
          aria-label={t("promptRecipes.chrome.close", { defaultValue: "Close prompt recipes" })}
          className="flex h-[22px] w-[22px] items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.2} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-0.5 px-2 py-1.5">
        {([
          { key: "all", label: t("promptRecipes.chrome.allTab", { defaultValue: "All" }) },
          ...categories
        ] as { key: TabKey; label: string }[]).map(
          (tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                "rounded-full px-2 py-[3px] text-[10px] font-bold uppercase tracking-[0.07em] transition-colors",
                activeTab === tab.key
                  ? "bg-orange-active text-orange"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
            >
              {tab.label}
            </button>
          )
        )}
      </div>

      {/* List */}
      <div ref={listRef} className="min-h-0 overflow-y-auto p-1.5" style={{ maxHeight: listMaxHeight }}>
        {orderedCategories.map(({ key, label }) => {
          const recipes = visibleRecipes.filter((r) => r.category === key);
          if (recipes.length === 0) return null;
          return (
            <div key={key}>
              <div className="px-2 pb-1 pt-[7px] text-[10px] font-bold uppercase tracking-[0.07em] text-muted-foreground">
                {label}
              </div>
              {recipes.map((recipe) => {
                renderIndex += 1;
                const idx = renderIndex;
                const isActive = idx === activeIndex;
                return (
                  <button
                    key={recipe.id}
                    ref={(el) => {
                      itemRefs.current[idx] = el;
                    }}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    data-recipe-id={recipe.id}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => selectRecipe(recipe)}
                    className={cn(
                      "group flex w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-left transition-colors",
                      isActive ? "bg-orange-active" : "hover:bg-orange-active"
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-[30px] w-[30px] flex-shrink-0 items-center justify-center rounded-lg transition-colors",
                        isActive
                          ? "bg-orange-lighter text-orange"
                          : "bg-muted text-muted-foreground group-hover:bg-orange-lighter group-hover:text-orange"
                      )}
                    >
                      <SlashGlyph />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span
                        className={cn(
                          "block truncate text-[13px] font-semibold transition-colors",
                          isActive
                            ? "text-orange"
                            : "text-foreground group-hover:text-orange"
                        )}
                      >
                        {recipe.title}
                      </span>
                      <span className="mt-px block truncate text-[11px] font-normal text-muted-foreground">
                        {recipe.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );

  return anchorRef ? createPortal(popup, document.body) : popup;
};

export default PromptRecipesPopup;
